/**
 * Event taxonomy engine (Atlas 3.0 §L1).
 *
 * Classifies all manifest events into 4 safety tiers and produces:
 *   - Tier distribution (count per tier)
 *   - Per-event enriched records with PII guard status, dual-write flag,
 *     compliance refs, and subscriber mapping
 *   - Payload schema validation (required fields by event category)
 *   - Safety tier summary for the Event Intelligence module (M2)
 */
import type { AtlasManifest, EventType } from '@atlas-manifest/types';
import eventsCatalogJson from '@atlas-manifest/events-catalog.json';

// ─── Types ────────────────────────────────────────────────────────────────────

export type EventSafetyTierNum = 0 | 1 | 2 | 3;

export interface TierConfig {
  tier: EventSafetyTierNum;
  label: string;
  color: string;
  description: string;
  requiresDualWrite: boolean;
  excludeFromFirestore: boolean;
  storageTargets: string[];
}

export const TIER_CONFIGS: readonly TierConfig[] = [
  {
    tier: 0,
    label: 'Critical Safety',
    color: '#EF4444',
    description:
      'Irreversible financial mutations. Must emit event, dual-write to audit + operational store. Never replay without human review.',
    requiresDualWrite: true,
    excludeFromFirestore: false,
    storageTargets: ['firestore:auditLogs', 'firestore:events', 'bigquery'],
  },
  {
    tier: 1,
    label: 'Compliance',
    color: '#F59E0B',
    description:
      'Consent, PII, regulatory triggers. Must be durable, immutable, queryable for DSAR. Dual-write recommended.',
    requiresDualWrite: true,
    excludeFromFirestore: false,
    storageTargets: ['firestore:auditLogs', 'firestore:events', 'bigquery'],
  },
  {
    tier: 2,
    label: 'Operational',
    color: '#06B6D4',
    description: 'State transitions, user actions. Firestore write. Excluded from long-term audit.',
    requiresDualWrite: false,
    excludeFromFirestore: false,
    storageTargets: ['firestore:events'],
  },
  {
    tier: 3,
    label: 'Observability',
    color: '#6B7280',
    description:
      'Telemetry, performance traces, feature flag evaluations. High volume — excluded from Firestore. BigQuery only.',
    requiresDualWrite: false,
    excludeFromFirestore: true,
    storageTargets: ['bigquery'],
  },
];

export interface EnrichedEventType {
  eventType: EventType;
  resolvedTier: EventSafetyTierNum;
  tierConfig: TierConfig;
  /** Whether the event carries PII fields based on payload field names. */
  hasPiiPayload: boolean;
  /** PII field names detected in the payload. */
  piiPayloadFields: string[];
  /** Cloud functions that emit this event. */
  emittedBy: string[];
  /** Entity ids this event references. */
  linkedEntities: string[];
}

export interface TierDistribution {
  tier: EventSafetyTierNum;
  count: number;
  events: string[];
}

export interface EventTaxonomyReport {
  enriched: EnrichedEventType[];
  distribution: TierDistribution[];
  /** Events with PII payload but no complianceRef. */
  untaggedPiiEvents: string[];
  /** Events classified as Tier 0 or 1 but missing dual-write flag. */
  missingDualWrite: string[];
  /** Total event type count. */
  total: number;
}

// ─── PII payload field patterns ───────────────────────────────────────────────

const PII_PAYLOAD_PATTERNS = [
  /^email/i,
  /^phone/i,
  /^name/i,
  /^ssn/i,
  /^dob|dateOfBirth/i,
  /^address/i,
  /^plaidToken|accessToken/i,
  /^stripeId|customerId/i,
  /^routingNumber|accountNumber/i,
  /^ipHash|ipAddress/i,
  /^deviceId/i,
  /^uid$|userId/i, // uid is quasi-identifier when combined
];

function detectPiiPayloadFields(event: EventType): string[] {
  return event.payloadFields
    .filter((f) => PII_PAYLOAD_PATTERNS.some((p) => p.test(f.name)))
    .map((f) => f.name);
}

// ─── Tier resolution ──────────────────────────────────────────────────────────

/**
 * Resolve a manifest eventType's safety tier to a numeric 0–3 value.
 * Handles both the legacy string form and the new numeric form.
 */
function resolveTierNumber(event: EventType): EventSafetyTierNum {
  const raw = event.safetyTier;

  // Numeric form already
  if (typeof raw === 'number' && raw >= 0 && raw <= 3) {
    return raw as EventSafetyTierNum;
  }

  // Legacy string form
  if (raw === 'safety') return 0;
  if (raw === 'regular') return 2;

  // Heuristic classification by event type name
  const type = event.type;

  // Tier 0: irreversible financial mutations
  if (
    /payment|transfer|withdrawal|refund|charge|subscription_cancelled|account_closed/i.test(type)
  ) {
    return 0;
  }

  // Tier 1: compliance and regulatory
  if (
    /consent|gdpr|deletion|export|erasure|dsar|minor|age_gate|plaid_token|coppa|kosa/i.test(type)
  ) {
    return 1;
  }

  // Tier 3: observability
  if (
    /perf_trace|feature_flag|cache_invalidat|tab_sync|offline_action|telemetry/i.test(type)
  ) {
    return 3;
  }

  // Default: Tier 2 operational
  return 2;
}

// ─── Main classifier ──────────────────────────────────────────────────────────

export function classifyEventTaxonomy(manifest: AtlasManifest): EventTaxonomyReport {
  const tierMap = new Map(TIER_CONFIGS.map((t) => [t.tier, t]));

  // Build emitter map from cloudFunctions
  const emitterMap = new Map<string, string[]>();
  for (const fn of manifest.cloudFunctions) {
    for (const eventType of fn.emitsEvents ?? []) {
      const existing = emitterMap.get(eventType) ?? [];
      existing.push(fn.name);
      emitterMap.set(eventType, existing);
    }
  }

  const enriched: EnrichedEventType[] = [];
  const untaggedPiiEvents: string[] = [];
  const missingDualWrite: string[] = [];

  for (const event of manifest.events) {
    const resolvedTier = resolveTierNumber(event);
    const tierConfig = tierMap.get(resolvedTier)!;
    const piiPayloadFields = detectPiiPayloadFields(event);
    const hasPiiPayload = piiPayloadFields.length > 0;

    if (hasPiiPayload && !event.complianceRef) {
      untaggedPiiEvents.push(event.type);
    }

    if (
      (resolvedTier === 0 || resolvedTier === 1) &&
      !event.requiresDualWrite
    ) {
      missingDualWrite.push(event.type);
    }

    enriched.push({
      eventType: event,
      resolvedTier,
      tierConfig,
      hasPiiPayload,
      piiPayloadFields,
      emittedBy: emitterMap.get(event.type) ?? [],
      linkedEntities: event.carriedEntities,
    });
  }

  // Distribution
  const distribution: TierDistribution[] = ([0, 1, 2, 3] as EventSafetyTierNum[]).map((tier) => {
    const tierEvents = enriched.filter((e) => e.resolvedTier === tier);
    return {
      tier,
      count: tierEvents.length,
      events: tierEvents.map((e) => e.eventType.type),
    };
  });

  return {
    enriched,
    distribution,
    untaggedPiiEvents,
    missingDualWrite,
    total: manifest.events.length,
  };
}

/** Get tier config for a numeric tier. */
export function getTierConfig(tier: EventSafetyTierNum): TierConfig {
  return TIER_CONFIGS[tier];
}

/** Get enriched record for a single event type. */
export function getEnrichedEvent(
  report: EventTaxonomyReport,
  eventType: string,
): EnrichedEventType | null {
  return report.enriched.find((e) => e.eventType.type === eventType) ?? null;
}

/** Subset of events that must be dual-written (Tier 0 + Tier 1). */
export function getDualWriteEvents(report: EventTaxonomyReport): EnrichedEventType[] {
  return report.enriched.filter((e) => e.resolvedTier === 0 || e.resolvedTier === 1);
}

/** Events excluded from Firestore (Tier 3 observability). */
export function getFirestoreExcludedEvents(report: EventTaxonomyReport): EnrichedEventType[] {
  return report.enriched.filter((e) => e.resolvedTier === 3);
}

// ─── Semantic category classification ──────────────────────────────────────────

export interface CategoryConfig {
  category: string;
  label: string;
  expectedTotal: number;
  prefixes: string[];
}

/** Loaded from events-catalog.json — single source of truth for category config. */
export const EVENT_CATEGORIES: readonly CategoryConfig[] = eventsCatalogJson.categories.map((c) => ({
  category: c.id,
  label: c.label,
  expectedTotal: c.expectedTotal,
  prefixes: c.prefixes,
}));

/** Set of recognized catalog category ids — used to gate category-field classification. */
const KNOWN_CATEGORY_IDS = new Set(EVENT_CATEGORIES.map((c) => c.category));

export interface CategoryResult {
  category: string;
  label: string;
  expectedTotal: number;
  covered: number;
  pct: number;
}

/**
 * Classify events by semantic category. Uses the `category` field set by the
 * manifest parser (seeded from events-catalog.json), falling back to prefix
 * matching for any events that pre-date the catalog.
 */
export function classifyEventsByCategory(manifest: AtlasManifest): CategoryResult[] {
  const events = manifest.events ?? [];
  return EVENT_CATEGORIES.map((cat) => {
    const covered = events.filter((e) => {
      // Use the catalog category field when it's a recognized category id.
      // Fall back to prefix matching for unrecognized or absent categories.
      if (e.category && KNOWN_CATEGORY_IDS.has(e.category)) {
        return e.category === cat.category;
      }
      return cat.prefixes.some((prefix) => e.type.startsWith(prefix));
    }).length;
    const pct = Math.round((covered / cat.expectedTotal) * 100);
    return {
      category: cat.category,
      label: cat.label,
      expectedTotal: cat.expectedTotal,
      covered,
      pct,
    };
  });
}

// ─── ROGUE event detection ────────────────────────────────────────────────────

export interface RogueEventDef {
  id: 'R1' | 'R2' | 'R3';
  eventType: string;
  defect: string;
  impact: string;
  fix: string;
}

export const ROGUE_EVENT_DEFS: readonly RogueEventDef[] = [
  {
    id: 'R1',
    eventType: 'mutation_conflict',
    defect: 'Payload typed as `any` — bypasses per-tool Zod schema validation.',
    impact: 'Corrupt optimistic rollback payloads can silently overwrite valid Firestore docs.',
    fix: 'Replace with discriminated union MutationConflictEvent<T>; enforce via TOOL_PARAM_SCHEMAS.',
  },
  {
    id: 'R2',
    eventType: 'integration_connected',
    defect: 'Schema collapse — Plaid, Stripe, and native integrations share one event type with optional provider fields.',
    impact: 'Consumer code branches on provider string at runtime; new providers silently ignored.',
    fix: 'Split into plaid_connected / stripe_account_linked / native_integration_enabled with per-type Zod schemas.',
  },
  {
    id: 'R3',
    eventType: 'msg_submitted',
    defect: 'Subscriber writes directly to Firestore inside the event handler, bypassing offline queue and DLQ.',
    impact: 'Under network loss, message appears submitted but is not persisted.',
    fix: 'Move Firestore write to executeOptimistic() before emitting; emit event post-write with doc ID in payload.',
  },
] as const;

export interface RogueEventResult extends RogueEventDef {
  present: boolean;
}

export function detectRogueEvents(manifest: AtlasManifest): RogueEventResult[] {
  const eventTypes = new Set((manifest.events ?? []).map((e) => e.type));
  return ROGUE_EVENT_DEFS.map((def) => ({
    ...def,
    present: eventTypes.has(def.eventType),
  }));
}
